import {BaseElement, BaseElementAsync, isEltInitableAsyncPending, OSkinableInit} from "back/commons/basis";
import {Area, IArea, IAreaPointer, isAreaPointer} from "lib/commons/areas";
import {LANG} from "lib/commons/lang";
import {LASTDATAS, OLastDatasInit} from "lib/commons/lastDatas";
import {IReg} from 'lib/commons/registry';
import {DOM, INodeFilter} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";


/**
 * Api optionnelles de pilotage des Views.
 * Ces méthodes sont implémentables sur les IViews elles-memes ou des controlers ou containers de Views.
 */
export interface IViewApi {

	/** Méthode implémentable par une view pour être informée que la view vient d'être affichée. */
	onViewShown?(): void

	/**
	 * Méthode implémentable par une view pour intercepter son masquage ou sa suppression et éventuellement le refuser.
	 * @return Si true, la view peut-être masquée. Si false la view ne peut pas être masquée,
	 *    à moins d'essayer via onViewWaitForHide() si la méthode existe.
	 * @see VIEWS.canCloseView(view)
	 */
	onViewBeforeHide?(close?: boolean): boolean

	/**
	 * Attend une confirmation user (ou la validation d'un enregistrement async) avant de pouvoir masquer (ou supprimer)
	 * cette view (si la promesse retourne true).
	 * Si la promesse retourne false, le masquage ou sa suppression doit être abandonné.
	 * La méthode onViewBeforeHide() DOIT être implémentée (et retourner false) pour que celle-ci soit exploitée.
	 *
	 * @see VIEWS.canCloseView(view)
	 */
	onViewWaitForHide?(close?: boolean): Promise<boolean>

	/**
	 * Méthode implémentable par une view pour être informée qu'elle a été masquée, voire définitivement supprimée.
	 * Dans le cas général, onViewBeforeHide() et onViewWaitForHide() ont été appelé précédemment pour validation.
	 * Mais dans certains cas critiques (logout externe...), cette méthode peut-être appelée
	 * directement sans être passé via onViewBeforeHide() et onViewWaitForHide().
	 *
	 * @param closed Si true indique que cette view ne sera jamais réutilisée et doit libérer toutes
	 *        les ressources et abonnements externes.
	 */
	onViewHidden?(closed?: boolean): void

}

/**
 * Une IView peut être n'importe quel élément HTML. Cette interface ajoute des méthodes optionnelles qui doivent
 * être appelées par le IViewContainer lorsqu'elles existent.
 *
 * Attributs standards d'une IView :
 *  - code <- Area.getId()
 *  - icon <- Area.getIcon(ctx)
 *  - label <- Area.getLabel(ctx)
 *  - description <- Area.getTitle(ctx)
 *  - hidden <- !Area.isVisible(ctx)
 *  - disabled <- !Area.isEnabled(ctx)
 *
 *  Tous ces attributs sont optionnels.
 *  L'obtention de ces propriétés doit passer par VIEWS.getXxxx(view).
 *
 * Si un de ces attributs est modifié, VIEWS.dispatchViewChange(this) DOIT être appelé.
 */
export interface IView extends HTMLElement, IViewApi {}


/** Container de plusieurs views (tabs, etc). */
export interface IViewsContainer {
	/**
	 * Visite les views de ce container.
	 * Dès que la fonction retourne quelquechose != undefined, le parcours s'interrompt et la valeur est retournée.
	 *
	 * Si la view passée en paramètre est un IViewsContainer, c'est au visitor d'appeler view.visitViews() et
	 * de (re)définir lui-même ses OViewVisitOptions.
	 */
	visitViews(visitor: (view: IViewApi) => any, options?: OViewVisitOptions): any

	/**
	 * Visite les views de ce container séquentiellement avec résolution des promesses une à une.
	 * Dès que la fonction retourne quelquechose != undefined, le parcours s'interrompt et la valeur est retournée.
	 *
	 * Si la view passée en paramètre est un IViewsContainer, c'est au visitor d'appeler view.visitViewsAsync() et
	 * de (re)définir lui-même ses OViewVisitOptions.
	 */
	visitViewsAsync(visitor: (view: IViewApi) => Promise<any>, options?: OViewVisitOptions): Promise<any>
}

export function isViewsContainer(ctn: any): ctn is IViewsContainer {return ctn && typeof ctn.visitViews === 'function'}

/** Container restreint à une seule view. */
export interface IViewContainer extends IViewsContainer {
	view: IView
}

/** Options pour visiter les views d'un container. */
export interface OViewVisitOptions {
	/** Si true, ne visite que les views affichées (non masquées). */
	visible?: boolean
}

export interface OViewsContainerInit {
	views?: IView[]
}

/**
 * Container de vues génériques qui visite soit les vues passant en init, soit ses descendants
 */
export class ViewsContainer extends BaseElement implements IViewsContainer {
	views: IView[];

	protected _initialize(init: OViewsContainerInit): void {
		this.views = init.views;
	}

	visitViews(visitor: (view: IViewApi) => any): any {
		if (this.views) for (const view of this.views) {
			const r = visitor(view);
			if (r) return r;
		} else {
			return VIEWS.visitDescendants(this, visitor);
		}
	}

	async visitViewsAsync(visitor: (view: IViewApi) => Promise<any>): Promise<any> {
		if (this.views) for (const view of this.views) {
			const r = await visitor(view);
			if (r) return r;
		} else {
			return VIEWS.visitDescendantsAsync(this, visitor);
		}
	}
}

customElements.define('c-views-ctn', ViewsContainer);

/**
 * Impl de base d'une IView avec des propriétés correspondant aux attributs standard
 * qui assument l'appel à VIEWS.dispatchViewChange(this) à l'affectation de valeurs.
 *
 * Note : Dans certains cas, l'event ViewChange peut être dispatché une fois alors qu'il n'y a
 * pas eu de réel chgt de valeur (si égal à la value issue de l'area).
 */
export interface BaseAreaView<C> extends BaseElement {
	initialize(init?: OBaseAreaViewInit<C>): this
}

export interface OBaseAreaViewInit<C> extends OSkinableInit, IAreaPointer<C>, OLastDatasInit {
}

export class BaseAreaView<C> extends BaseElement implements IView, IAreaPointer<C> {

	reg: IReg<any>;
	area: IArea<C>;
	areaContext: C;

	get icon(): string {return VIEWS.getIcon(this)}

	set icon(val: string) {
		if (DOM.setAttr(this, 'icon', val)) VIEWS.dispatchViewChange(this);
	}

	get label(): string {return VIEWS.getLabel(this)}

	set label(val: string) {
		if (DOM.setAttr(this, 'label', val)) VIEWS.dispatchViewChange(this);
	}

	get description(): string {return VIEWS.getDescription(this)}

	set description(val: string) {
		if (DOM.setAttr(this, 'description', val)) VIEWS.dispatchViewChange(this);
	}

	get disabled(): boolean {return !VIEWS.isEnabled(this)}

	set disabled(val: boolean) {
		if (DOM.setAttrBool(this, 'disabled', val)) VIEWS.dispatchViewChange(this);
	}

	get hidden(): boolean {return !VIEWS.isVisible(this)}

	set hidden(val: boolean | null) {
		if (DOM.setHidden(this, val)) VIEWS.dispatchViewChange(this);
	}

	protected _initialize(init: OBaseAreaViewInit<C>) {
		if (init.area) this.area = init.area;
		if (!this.areaContext) this.areaContext = init.areaContext || this as any; //Si pas de ctx, this par défaut.
		if (init.lastDatasKey) this.setAttribute("last-datas", init.lastDatasKey);
		if (!this.reg) this.reg = this.findReg(init);
	}

	buildInitFromAtts(init?: OBaseAreaViewInit<C>): OBaseAreaViewInit<C> {
		init = super.buildInitFromAtts(init);
		const reg = this.findReg(init);
		const areaCtx = this.getAttribute('area-context');
		if (areaCtx) init.areaContext = reg.getSvc<C>(areaCtx);
		const area = this.getAttribute('area');
		if (area) init.area = reg.getSvc<Area<C, this>>(area);
		init.lastDatas = LASTDATAS.getLastDatas(this, this.getAttribute("last-datas"));
		return init;
	}
}

/**
 * Base pour un élément Async de type View fondé sur une Area.
 *
 * La méthode _initialize():Promsie<void> DOIT être implémentée.
 * Elle peut appeler super._initialize() pour initialiser area, areaContext et reg,
 * mais doit construire son propre Promise<void> à retourner.
 */
export class BaseAreaViewAsync<C> extends BaseElementAsync implements IView {
	reg: IReg<any>;
	area: IArea<C>;
	areaContext: C;
}

LANG.completeClassProps(BaseAreaView, BaseAreaViewAsync);

export namespace VIEWS {

	/** Recherche le code d'une view. */
	export function getCode(view: IView): string | null {
		const code = view.getAttribute('code');
		if (code != null) return code;
		if (isAreaPointer(view)) return view.area.getId();
		return null;
	}

	/** Recherche le label d'une view. */
	export function getLabel(view: IView): string | null {
		const label = view.getAttribute('label');
		if (label != null) return label;
		if (isAreaPointer(view)) return view.area.getLabel(view.areaContext);
		return null;
	}

	/** Recherche l'icone d'une view. */
	export function getIcon(view: IView): string | null {
		const icon = view.getAttribute('icon');
		if (icon != null) return icon;
		if (isAreaPointer(view)) return view.area.getIcon(view.areaContext);
		return null;
	}

	/** Recherche la description d'une view. */
	export function getDescription(view: IView): string | null {
		const desc = view.getAttribute('description');
		if (desc != null) return desc;
		if (isAreaPointer(view)) return view.area.getDescription(view.areaContext);
		return null;
	}

	export function isVisible(view: IView): boolean {
		if (view.hasAttribute('hidden')) return false;
		if (isAreaPointer(view)) return view.area.isVisible(view.areaContext);
		return true;
	}

	/** Cette view est-elle active ? */
	export function isEnabled(view: IView): boolean {
		if (view.hasAttribute('disabled')) return false;
		if (isAreaPointer(view)) return view.area.isEnabled(view.areaContext);
		return false;
	}

	/**
	 * A appeler par la view lorsqu'elle  modifie une ou plusieurs de ses propriétés publiques
	 * (icon, label, visible, enabled) pour en informer les containers.
	 */
	export function dispatchViewChange(view: IView) {
		view.dispatchEvent(new CustomEvent("c-view-change", {/*composed: true, utile?*/ bubbles: true}));
	}

	export function onViewShown(view: IViewApi | IView | IViewsContainer | null): void {
		if (view && ('onViewShown' in view)) {
			if (isEltInitableAsyncPending(view)) view.initializedAsync.then(() => {(view as IView).onViewShown()});
			else view.onViewShown();
		} else if (isViewsContainer(view)) onContainerShown(view);
	}

	export function onContainerShown(ctn: IViewsContainer): void {
		if (isEltInitableAsyncPending(ctn)) ctn.initializedAsync.then(() => {(ctn as IViewsContainer).visitViews(onViewShown, VISIT_VISIBLES)});
		else ctn.visitViews(onViewShown, VISIT_VISIBLES);
	}

	export async function canHideViewSilently(view: IViewApi | IView | IViewsContainer, close?: boolean): Promise<boolean> {
		if (!view) return true;
		if (isEltInitableAsyncPending(view)) await view.initializedAsync;
		if ('onViewBeforeHide' in view) return view.onViewBeforeHide(close);
		if (isViewsContainer(view)) return canHideContainerSilently(view, close);
		return true;
	}

	export async function canHideContainerSilently(ctn: IViewsContainer, close?: boolean): Promise<boolean> {
		if (isEltInitableAsyncPending(ctn)) await ctn.initializedAsync;
		return await ctn.visitViewsAsync(async (v) => await canHideViewSilently(v, close) ? undefined : false, close ? null : VISIT_VISIBLES) !== false;
	}

	export async function canHideView(view: IViewApi | IView | IViewsContainer, close?: boolean, beforeAsyncCheck?: () => void): Promise<boolean> {
		if (!view) return true;
		if (isEltInitableAsyncPending(view)) await view.initializedAsync;
		if ('onViewBeforeHide' in view) {
			if (view.onViewBeforeHide(close)) return true;
			if ('onViewWaitForHide' in view) {
				if (beforeAsyncCheck) beforeAsyncCheck();
				return view.onViewWaitForHide(close);
			}
			return false;
		}
		if (isViewsContainer(view)) return canHideContainer(view, close);
		return true;
	}

	export async function canHideContainer(ctn: IViewsContainer, close?: boolean, beforeAsyncCheck?: () => void): Promise<boolean> {
		if (isEltInitableAsyncPending(ctn)) await ctn.initializedAsync;
		return await ctn.visitViewsAsync(async (v) => await canHideView(v, close, beforeAsyncCheck) ? undefined : false, close ? null : VISIT_VISIBLES) !== false;
	}

	export function onViewHidden(view: IViewApi | IView | IViewsContainer | null, closed?: boolean): void {
		if (view && ('onViewHidden' in view)) {
			if (isEltInitableAsyncPending(view)) view.initializedAsync.then(() => {(view as IView).onViewHidden(closed)});
			else view.onViewHidden(closed);
		} else if (isViewsContainer(view)) onContainerHidden(view, closed);
	}

	export async function onContainerHidden(ctn: IViewsContainer, closed?: boolean) {
		if (isEltInitableAsyncPending(ctn)) await ctn.initializedAsync;
		ctn.visitViews((v) => {onViewHidden(v, closed)}, closed ? null : VISIT_VISIBLES);
	}

	export function visitDescendants(from: Node, visitor: (view: IViewApi) => any): any {
		const tw = document.createTreeWalker(from, NodeFilter.SHOW_ELEMENT);
		if (!tw.nextNode()) return;
		do {
			const r = visitor(tw.currentNode as IViewApi);
			if (r !== undefined) return r;
			if (isViewsContainer(tw.currentNode)) {
				//Le visitor a du traiter la descendance, on saute les fils
				while (!tw.nextSibling()) if (!tw.parentNode()) return;
			} else {
				if (!tw.nextNode()) return;
			}
		} while (true);
	}

	export async function visitDescendantsAsync(from: Node, visitor: (view: IViewApi) => Promise<any>): Promise<any> {
		const tw = document.createTreeWalker(from, NodeFilter.SHOW_ELEMENT);
		do {
			const r = await visitor(tw.currentNode as IViewApi);
			if (r !== undefined) return r;
			if (isViewsContainer(tw.currentNode)) {
				//Le visitor a du traiter la descendance, on saute les fils
				while (!tw.nextSibling()) if (!tw.parentNode()) return;
			} else {
				if (!tw.nextNode()) return;
			}
		} while (true);
	}

	export function visitNodes(visitor: (view: IViewApi) => any, options: OViewVisitOptions, ...nodes: IViewApi[]): any {
		for (const n of nodes) {
			if (n) {
				const r = visitor(n);
				if (r !== undefined) return r;
			}
		}
	}

	export async function visitNodesAsync(visitor: (view: IViewApi) => any, options: OViewVisitOptions, ...nodes: IViewApi[]): Promise<any> {
		for (const n of nodes) {
			if (n) {
				const r = await visitor(n);
				if (r !== undefined) return r;
			}
		}
	}

	/**
	 * Supprime tous les fils sauf les balises <style> avec appel à l'API IView 'onViewHidden'.
	 * @see BASIS.clearContent()
	 *
	 * @param closed généralement true, false si les noeuds peuvent être réemployées.
	 */
	export function clearContent(parent: ShadowRoot | Node, closed: boolean) {
		let ch = parent.lastChild;
		while (ch) {
			if (ch.localName !== "style") {
				const prev = ch.previousSibling;
				if ('onViewHidden' in ch) onViewHidden(ch as IView, closed);
				else if (isViewsContainer(ch)) onContainerHidden(ch, closed);
				else visitDescendants(ch, (n) => onViewHidden(n, closed));
				parent.removeChild(ch);
				ch = prev;
			} else {
				ch = ch.previousSibling;
			}
		}
	}

	/** Affecte l'area et l'areaContext sur une view nouvellement instanciée. */
	export function setArea(view: IView, areaPointer: IAreaPointer) {
		(view as IAreaPointer).area = areaPointer.area;
		(view as IAreaPointer).areaContext = areaPointer.areaContext;
	}

	export const VISIT_VISIBLES: OViewVisitOptions = {visible: true};
}
